import { ref, readonly } from 'vue';
import { cashInService } from '@/modules/cashIn/services/cashIn.service';
import { toast } from '@flash-global66/g-toast';
import useLoader from '@/composables/useLoader';
import { getReasonMessage } from '@/shared/common';
import type { TypeKushkiBanks, TypeKushkiResponse } from '@/types/PaymentTypes';
import type PaymentType from '@/types/PaymentTypes';
import type { PaymentBanksByBranch, PaymentBanksByBranchParams } from '@/api/types/cashin/PaymentMethods';

export function useCashInPaymentMethods() {
  const { showGLoader } = useLoader();
  
  const kushkiBanks = ref<TypeKushkiBanks[]>([]);
  const paymentBanks = ref<PaymentBanksByBranch[]>([]);
  const customerPaymentMethods = ref<PaymentType[]>([]);
  const isLoading = ref(false);
  const error = ref('');

  /**
   * Obtiene la lista de bancos Kushki
   * @param options - Opciones adicionales (showToast, callbacks)
   */
  async function loadKushkiBanks(
    options: {
      showToast?: boolean;
      onSuccess?: (banks: TypeKushkiBanks[]) => void;
      onError?: (errorMessage: string) => void;
    } = {}
  ) {
    const { showToast = false, onSuccess, onError } = options;
    
    isLoading.value = true;
    showGLoader(true);
    error.value = '';

    const result = await cashInService.getKushkiBankList();
    
    const returnValue = result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'No se pudieron obtener los bancos de depósito. Inténtalo nuevamente'
        });
        error.value = errorMessage;
        if (showToast) toast.error(errorMessage);
        onError?.(errorMessage);
        kushkiBanks.value = [];
        return undefined;
      },
      (success) => {
        kushkiBanks.value = success.data;
        onSuccess?.(success.data);
        return success.data;
      }
    );

    isLoading.value = false;
    showGLoader(false);
    
    return returnValue;
  }

    /**
   * Obtiene los datos de transferencia de Kushki por token.
   * @param params - Parámetros de la consulta (token, callbacks, opciones)
   * @returns Datos de la transferencia o null si hay error.
   */
  async function getKushkiTransferData(
    params: {
      token: string;
      showToast?: boolean;
      onSuccess?: (data: TypeKushkiResponse) => void;
      onError?: (errorMessage: string) => void;
    }
  ): Promise<TypeKushkiResponse | null> {
    const { token, showToast = false, onSuccess, onError } = params;
    
    isLoading.value = true;
    showGLoader(true);
    error.value = '';

    const result = await cashInService.getKushkiTransferData(token);
    
    const returnValue = result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Error al obtener los datos de la transferencia'
        });
        error.value = errorMessage;
        if (showToast) toast.error(errorMessage);
        onError?.(errorMessage);
        return null;
      },
      (success) => {
        onSuccess?.(success.data);
        return success.data;
      }
    );

    isLoading.value = false;
    showGLoader(false);
    
    return returnValue;
  }

  /**
   * Obtiene bancos de pago por sucursal
   * @param params - Parámetros para filtrar bancos por sucursal
   * @param onSuccess - Callback opcional ejecutado en caso de éxito
   * @param onError - Callback opcional ejecutado en caso de error
   */
  /**
   * Obtiene bancos de pago por sucursal
   * @param params - Parámetros de búsqueda de bancos y opciones
   */
  async function loadPaymentBanksByBranch(
    params: {
      product: string;
      branch: 'GLOBAL_CARD' | 'G81';
      countryCode: string;
      currencyCode: string;
      showToast?: boolean;
      onSuccess?: (banks: PaymentBanksByBranch[]) => void;
      onError?: (errorMessage: string) => void;
    }
  ) {
    const { product, branch, countryCode, currencyCode, showToast = false, onSuccess, onError } = params;
    
    isLoading.value = true;
    showGLoader(true);
    error.value = '';

    const bankParams: PaymentBanksByBranchParams = {
      product,
      branch,
      countryCode,
      currencyCode,
    };

    const result = await cashInService.getPaymentBanksByBranch(bankParams);
    
    const returnValue = result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Ha ocurrido un error al obtener los bancos'
        });
        error.value = errorMessage;
        if (showToast) toast.error(errorMessage);
        onError?.(errorMessage);
        paymentBanks.value = [];
        return undefined;
      },
      (success) => {
        paymentBanks.value = success.data;
        onSuccess?.(success.data);
        return success.data;
      }
    );

    isLoading.value = false;
    showGLoader(false);
    
    return returnValue;
  }

  /**
   * Obtiene los métodos de pago disponibles para un cliente
   * @param routeId - ID de la ruta
   * @param originAmountUSD - Monto en USD de origen
   * @param options - Opciones adicionales (showToast, callbacks)
   */
  async function loadCustomerPaymentMethods(
    routeId: number,
    originAmountUSD: number,
    options: {
      showToast?: boolean;
      onSuccess?: (methods: PaymentType[]) => void;
      onError?: (errorMessage: string) => void;
    } = {}
  ) {
    const { showToast = false, onSuccess, onError } = options;
    
    isLoading.value = true;
    showGLoader(true);
    error.value = '';

    const result = await cashInService.getCustomerPaymentMethod({ routeId, originAmountUSD });
    
    const returnValue = result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Ha ocurrido un error'
        });
        error.value = errorMessage;
        if (showToast) toast.error(errorMessage);
        onError?.(errorMessage);
        customerPaymentMethods.value = [];
        return undefined;
      },
      (success) => {
        customerPaymentMethods.value = success.data;
        onSuccess?.(success.data);
        return success.data;
      }
    );

    isLoading.value = false;
    showGLoader(false);
    
    return returnValue;
  }

  return {
    kushkiBanks: readonly(kushkiBanks),
    paymentBanks: readonly(paymentBanks),
    customerPaymentMethods: readonly(customerPaymentMethods),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    loadKushkiBanks,
    getKushkiTransferData,
    loadPaymentBanksByBranch,
    loadCustomerPaymentMethods,
  };
}
